import { Box, Button, Card, Stack, Text, useMantineTheme } from '@mantine/core'
import { useGetLocation } from 'api/locations'
import { DeviceMarker } from 'components/map/DeviceMarker'
import { LatLng, LatLngBounds, latLng } from 'leaflet'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Polygon, useMap } from 'react-leaflet'
import { Link, To } from 'react-router-dom'
import { TDevice } from 'shared/types'
import { useCustomizerStore } from 'store/customizerStore'
import { CustomTileLayer, SafeMapContainer } from '..'
import classes from './DeviceLocationCard.module.css'

export const DeviceLocationCard = ({
  editLink,
  ...device
}: TDevice & { editLink: To }) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()

  const position =
    device.localization.latitude &&
    device.localization.longitude &&
    latLng(
      device.localization.latitude,
      device.localization.longitude,
      device.localization.altitude,
    )

  return (
    <Box className={classes.root}>
      {!position && !device?.locationId && (
        <Box className={classes.emptyState}>
          <Card radius={'lg'}>
            <Stack align="center">
              <Text size={'sm'}>{t('location.noDataSet')}</Text>
              <Button component={Link} size="sm" variant="light" to={editLink}>
                {t('location.set')}
              </Button>
            </Stack>
          </Card>
        </Box>
      )}
      <div style={{ opacity: position || device?.locationId ? 1 : 0.4 }}>
        <SafeMapContainer
          center={position || [50, 22]}
          zoom={18}
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: theme.radius.md,
            zIndex: 1,
          }}
        >
          <DeviceMarker noPopup {...device} />
          {device?.locationId && (
            <LocationBoundaries locationId={device?.locationId} />
          )}
          <InvalidateMapOnViewChange />
          <CustomTileLayer />
        </SafeMapContainer>
      </div>
    </Box>
  )
}

// To fix a bug in leaflet rendering tiles off viewport
const InvalidateMapOnViewChange = () => {
  const map = useMap()
  const extendedView = useCustomizerStore(state => state.extendedView)

  useEffect(() => {
    if (!map || !extendedView) return

    const timeout = setTimeout(() => map.invalidateSize(), 500)

    return () => {
      clearTimeout(timeout)
    }
  }, [map, extendedView])

  return null
}

const LocationBoundaries = ({ locationId }: { locationId?: string }) => {
  const map = useMap()
  const { data, isSuccess } = useGetLocation(locationId)
  const polygonPositions = useMemo(
    () =>
      isSuccess && Array.isArray(data.boundaries)
        ? data.boundaries.map(
            point => new LatLng(point.latitude, point.longitude),
          )
        : null,
    [data, isSuccess],
  )
  const polygonBounds = useMemo(
    () =>
      isSuccess && Array.isArray(data.boundaries)
        ? new LatLngBounds(
            data.boundaries.map(point => [point.latitude, point.longitude]),
          )
        : null,
    [data, isSuccess],
  )

  useEffect(() => {
    if (!map || !polygonBounds) return

    map.fitBounds(polygonBounds)
  }, [map, polygonBounds])

  if (!polygonPositions) return null
  return <Polygon positions={polygonPositions} />
}
